import React, { useState, useEffect, Fragment } from 'react';
import { useHistory, Link, useParams } from 'react-router-dom';
import Select from 'react-select';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Button, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyrightIcon from '@mui/icons-material/Copyright';
// Assume fetchData and postData are defined elsewhere
import { fetchData, updateData, postData, VerifyData } from '../../../apiService';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
} from "@mui/material";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toWords } from 'number-to-words';
import Logo from './../Logo/QR.png';
import Modal from '@mui/material/Modal';

const ProformaDetails = () => {

    const { id } = useParams(); // Use useParams to get the id from the URL 
    const history = useHistory();
    const { isAuthenticated, companyId, userId, permissions, token, logout, branchstate, branchId } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated]);

    const [formData, setFormData] = useState({
        CompanyId: companyId,
        CreatedBy: userId,
    });

    const [selectedItems, setSelectedItems] = useState([]);

    const formatDateForInput = (dateString) => {
        const currentDate = new Date(dateString);;
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;

        return formattedDate;
    };

    //handle search [ITEM SEARCH]    
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const filteredData = selectedItems.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    useEffect(() => {
        fetchData('ProformaApi', `/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.Proforma);
                    setSelectedItems(response.response_data.ProformaDetails);
                }
            })
            .catch((error) => {
                console.error('Error fetching Proforma Details:', error);
            });
    }, [id]);

    const openModalverify = () => setIsModalOpenverify(true);
    const closeModalverify = () => setIsModalOpenverify(false);
    const [isModalOpenverify, setIsModalOpenverify] = useState(false);

    const handleSubmit = () => {

        VerifyData('ProformaAPI', `${id}`, token)
            .then((response) => {

                if (response.response === 'Proforma Converted') {
                    const SaleId = response.kotId;
                    handlePrint(SaleId); // Handle print with SaleId
                }
                history.push("/SaleIndex");
            })
            .catch((error) => {
                console.error('Error Converted to Purchase record:', error);
            });

    };

    const handlePrint = async (SALEID) => {
        try {
            // ------------------ FETCH DATA ------------------
            const response = await fetchData('ProformaApi', `/SalePrint/${SALEID}`, token);
            const labelData = response.response_data;

            if (!labelData?.SalePrint || !labelData?.SalePrintItems) {
                throw new Error("Invalid invoice data.");
            }

            const header = labelData.SalePrint;
            const items = labelData.SalePrintItems;

            // ------------------ INITIALIZE PDF ------------------
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [80, 280] // POS roll size
            });

            const margin = { top: 5, left: 0.8, right: 0.8 };
            const pageWidth = doc.internal.pageSize.getWidth();
            const usableWidth = pageWidth - margin.left - margin.right;
            let y = margin.top;

            const yStartOuter = margin.top - 1;

            // ------------------ HEADER ------------------
            y += 2;
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(`GSTIN : ${header.CompanyGSTNumber || ''}`, margin.left + 1, y);
            doc.text(`Ph : ${header.CompanyContactNumber1 || ''}`, pageWidth - margin.right - 1, y, { align: 'right' });
            y += 6;

            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(header.CompanyName || '', pageWidth / 2, y, { align: 'center' });
            y += 6;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            const addrLines = doc.splitTextToSize(header.CompanyAddressLine || '', usableWidth);
            addrLines.forEach(line => {
                doc.text(line, pageWidth / 2, y, { align: 'center' });
                y += 3;
            });

            doc.setLineWidth(0.1);
            doc.line(margin.left, y, pageWidth - margin.right, y);
            y += 3;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text("Proforma", pageWidth / 2, y, { align: 'center' });
            y += 3;

            // ------------------ BILL INFO + CUSTOMER ------------------
            const boxYStart = y;
            y += 3;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);

            const invNo = header.InvoiceNo || [header.BillNumber, header.FinYear].filter(Boolean).join('/');
            const billDate = header.BillDate ? new Date(header.BillDate).toLocaleDateString('en-GB') : '';
            const billTime = header.BillTime;

            doc.text(`Bill No: ${invNo}`, margin.left + 1, y);
            doc.text(`Date: ${billDate}`, pageWidth - margin.right - 1, y, { align: 'right' });
            y += 3;
            doc.text(`Time: ${billTime}`, pageWidth - margin.right - 1, y, { align: 'right' });
            y += 4;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text("Customer Details:", margin.left + 1, y);
            y += 3;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.text(`Name: ${header.CustomerName || ''}`, margin.left + 1, y);
            y += 3;

            if (header.CustomerAddress) {
                doc.text(`Address: ${header.CustomerAddress}`, margin.left + 1, y);
                y += 3;
            }

            if (header.CustomerContactNumber) {
                doc.text(`Contact: ${header.CustomerContactNumber}`, margin.left + 1, y);
                y += 3;
            }

            const boxYEnd = y + 1;
            doc.setLineWidth(0.1);
            doc.rect(0.8, boxYStart - 1, pageWidth - 1.6, boxYEnd - boxYStart + 1);
            y = boxYEnd;

            // ------------------ ITEMS TABLE ------------------
            const filteredItems = items.filter(i => (i.Quantity ?? 0) > 0);
            const rows = filteredItems.map((i, idx) => [
                idx + 1,
                i.ItemName || "",
                (i.Quantity ?? 0).toString(),
                (i.Rate ?? 0).toFixed(2),
                (i.DiscountAmount ?? 0).toFixed(2),
                (i.TotalAmount ?? 0).toFixed(2),
            ]);


            autoTable(doc, {
                startY: y,
                margin: { left: margin.left, right: margin.right },
                head: [["Sr", "Product", "Qty", "Rate", "Disc", "Amount"]],
                body: rows,
                theme: "grid",
                styles: {
                    fontSize: 8,
                    cellPadding: 1,
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1,
                    textColor: [0, 0, 0],
                    fontStyle: "bold", // ✅ bold for all item rows
                },
                headStyles: {
                    fillColor: [255, 255, 255],
                    textColor: [0, 0, 0],
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1,
                    fontStyle: "bold", // ✅ bold for header too
                },
                columnStyles: {
                    0: { halign: 'left' },
                    1: { halign: 'left' },
                    2: { halign: 'right' },
                    3: { halign: 'right' },
                    4: { halign: 'right' },
                    5: { halign: 'right' },
                },
            });


            y = doc.lastAutoTable.finalY + 6;

            // ------------------ TOTALS (Right Side Aligned, Left Labels) ------------------
            const leftX = margin.left + 45;
            const rightX = pageWidth - margin.right - 1.5;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);

            // Taxable Amount
            doc.text(`Taxable Amt`, leftX, y);
            doc.text(`${(Number(header.TaxableAmount) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;

            // CGST Amount
            doc.text(`CGST Amt`, leftX, y);
            doc.text(`${(Number(header.CGSTAmount) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;

            // SGST Amount
            doc.text(`SGST Amt`, leftX, y);
            doc.text(`${(Number(header.SGSTAmount) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;

            // Round Off
            doc.text(`Round Off`, leftX, y);
            doc.text(`${(Number(header.RoundOff) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;

            // Total Amount (bold)
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text(`Total Amt`, leftX, y);
            doc.text(`${(Number(header.GrandTotal) || 0).toFixed(2)}`, rightX, y, { align: 'right' });
            y += 4;
            doc.setFont('helvetica', 'normal'); // reset font

            // ------------------ AMOUNT IN WORDS ------------------
            doc.setLineWidth(0.1);
            doc.line(margin.left, y, pageWidth - margin.right, y);
            y += 3;

            const grandTotal = Number(header.GrandTotal) || 0;
            const numberToWords = (num) => {
                const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
                const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                const inWords = (n) => {
                    if (n < 20) return a[n];
                    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
                    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + inWords(n % 100) : '');
                    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
                    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
                    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
                };
                const parts = num.toFixed(2).split('.');
                let words = inWords(Number(parts[0])) + ' Rupees';
                if (Number(parts[1]) > 0) words += ' and ' + inWords(Number(parts[1])) + ' Paise';
                return words + ' Only';
            };

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.text(`${numberToWords(grandTotal)}`, margin.left + 1, y);
            y += 2;

            // ------------------ TERMS & CONDITIONS ------------------
            doc.setLineWidth(0.1);
            doc.line(margin.left, y, pageWidth - margin.right, y);
            y += 3;

            doc.setFont('helvetica', 'bold');
            doc.text(" ", margin.left + 1, y);
            y += 2;

            // ------------------ QR + Tax Breakdown (side by side) ------------------
            const qrBoxWidth = pageWidth * 0.3; // ~30% width for QR
            const qrBoxHeight = 25;
            const tableStartX = qrBoxWidth + 5; // leave small gap between QR and table
            let sectionY = y;

            // --- QR ---
            try {
                doc.addImage(
                    Logo, "PNG",
                    margin.left + 1,
                    sectionY,
                    qrBoxWidth, qrBoxHeight
                );
            } catch (error) {
                console.warn("QR image missing", error);
                doc.setFontSize(6);
                doc.text("QR IMAGE MISSING", margin.left + 1 + qrBoxWidth / 2, sectionY + qrBoxHeight / 2, { align: 'center' });
            }

            // --- Tax Breakdown Table ---
            const filteredItem = items.filter(i => (i.Quantity ?? 0) > 0);
            const taxGroups = {};
            filteredItem.forEach(item => {
                const gst = item.GSTRate ?? 0;
                if (!taxGroups[gst]) taxGroups[gst] = { amount: 0, cgst: 0, sgst: 0 };
                taxGroups[gst].amount += item.TaxableAmount ?? 0;
                taxGroups[gst].cgst += item.CGSTAmount ?? 0;
                taxGroups[gst].sgst += item.SGSTAmount ?? 0;
            });

            const taxBreakdownRows = Object.entries(taxGroups).map(([percent, vals]) => ([
                percent,
                vals.amount.toFixed(2),
                vals.cgst.toFixed(2),
                vals.sgst.toFixed(2)
            ]));

            const totalRow = taxBreakdownRows.reduce((acc, row) => {
                for (let i = 1; i <= 3; i++) acc[i] += parseFloat(row[i]);
                return acc;
            }, ['Total', 0, 0, 0]).map((v, i) => i === 0 ? v : v.toFixed(2));

            taxBreakdownRows.push(totalRow);

            // Table layout
            const colWidths = [8, 13, 13, 13];
            const colTitles = ['%', 'Amount', 'CGST', 'SGST'];

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text('Tax Breakdown', tableStartX, sectionY);

            let tableY = sectionY + 2;

            // Header
            let colX = tableStartX;
            colTitles.forEach((title, i) => {
                doc.rect(colX, tableY, colWidths[i], 5);
                doc.text(title, colX + colWidths[i] / 2, tableY + 3.2, { align: 'center' });
                colX += colWidths[i];
            });

            // Body
            doc.setFont('helvetica', 'normal');
            let currentY = tableY + 5;
            taxBreakdownRows.forEach(row => {
                let rowX = tableStartX;
                row.forEach((cell, i) => {
                    doc.rect(rowX, currentY, colWidths[i], 5);
                    doc.text(cell.toString(), rowX + colWidths[i] / 2, currentY + 3.2, { align: 'center' });
                    rowX += colWidths[i];
                });
                currentY += 5;
            });

            // Update y for next section
            y = Math.max(sectionY + qrBoxHeight, currentY) + 6;


            // ------------------ FOOTER ------------------
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.text("THANK YOU - VISIT AGAIN", pageWidth / 2, y, { align: 'center' });

            const yEndOuter = y + 2;
            doc.setLineWidth(0.1);
            doc.rect(0.5, yStartOuter, pageWidth - 1, yEndOuter - yStartOuter);

            // ------------------ PRINT ------------------
            const blob = doc.output('blob');
            const blobUrl = URL.createObjectURL(blob);
            const win = window.open(blobUrl, '_blank');
            if (win) win.onload = () => {
                win.focus();
                win.print();
            };

        } catch (err) {
            console.error("Error generating invoice:", err);
        }
    };

    return (
        <div
            style={{
                fontFamily: "Segoe UI, sans-serif",
                fontSize: "13px",
                width: "100%",
                padding: "5px",
                marginTop: '-30px'
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "6px",
                    marginBottom: "13px",
                    flexWrap: "nowrap", // ❌ prevent wrapping
                    gap: "20px", // spacing between form and buttons
                }}
            >
                {/* Title */}
                <h6
                    className="uppercase tracking-wide font-serif text-black mt-2"
                    style={{ flex: "0 0 auto" }}
                >
                    Proforma Details
                </h6>

                {/* Form Grid */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "300px 140px 150px 120px 120px 120px", // ✅ fixed widths
                        gap: "13px",
                        alignItems: "end",
                        flex: "1", // occupy remaining space
                    }}
                >
                    {/* Party Name */}
                    <div>
                        <label
                            style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                marginBottom: "2px",
                                display: "block",
                            }}
                        >
                            Party Name
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            value={formData.CustomerName}
                            type="text"
                            placeholder="CustomerName"
                            name='CustomerName'
                            readOnly
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "13px",
                                height: "28px",
                                width: "100%",
                                padding: "0 6px",
                            }}
                        />
                    </div>

                    <div>
                        <label
                            style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                marginBottom: "2px",
                                display: "block",
                            }}
                        >
                            Ph.No
                        </label>
                        <input
                            type="text"
                            name="ContactNumber"
                            value={formData.ContactNumber}
                            //onChange={handleChange}
                            readOnly
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "13px",
                                height: "28px",
                                width: "100%",
                                padding: "0 6px",
                            }}
                        />
                    </div>

                    {/* Invoice Type */}
                    <div>
                        <label
                            style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                marginBottom: "2px",
                                display: "block",
                            }}
                        >
                            Invoice Type
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            value={formData.InvoiceTypeName}
                            type="text"
                            placeholder="InvoiceTypeName"
                            name='InvoiceTypeName'
                            readOnly
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "13px",
                                height: "28px",
                                width: "100%",
                                padding: "0 6px",
                            }}
                        />
                    </div>

                    {formData.InvoiceTypeId === 4 && (
                        <div>
                            <label
                                style={{
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    marginBottom: "2px",
                                    display: "block",
                                }}
                            >
                                State
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={formData.StateName}
                                type="text"
                                placeholder="StateName"
                                name='StateName'
                                readOnly
                                style={{
                                    border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    height: "28px",
                                    width: "100%",
                                    padding: "0 6px",
                                }}
                            />
                        </div>
                    )}

                    {/* Sale Date */}
                    <div>
                        <label
                            style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                marginBottom: "2px",
                                display: "block",
                            }}
                        >
                            Sale Date
                        </label>
                        <input
                            type="date"
                            name="BillDate"
                            value={formatDateForInput(formData.BillDate)}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "13px",
                                height: "28px",
                                width: "100%",
                                padding: "0 6px",
                            }}
                        />
                    </div>

                    {/* Bill Number */}
                    <div>
                        <label
                            style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                marginBottom: "2px",
                                display: "block",
                            }}
                        >
                            Bill Number
                        </label>
                        <input
                            type="text"
                            name="BillNumber"
                            value={`${formData.BillNumber || ""}/${formData.FinYear || ""}`}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "13px",
                                height: "28px",
                                width: "100%",
                                padding: "0 6px",
                            }}
                        />
                    </div>
                </div>



                {/* Buttons */}
                <div
                    className="flex items-center gap-3 mt-2 md:mt-0"
                    style={{ flexShrink: 0, whiteSpace: "nowrap" }} // ✅ keep in one line
                >
                    <Button
                        variant="contained"
                        onClick={openModalverify}
                        style={{
                            height: "25px",
                            minWidth: "80px",
                            borderRadius: "6px",
                            backgroundColor: "#1E3A8A",
                            color: "#FFFFFF",
                            fontWeight: 600,
                            fontSize: "13px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            textTransform: "none",
                        }}
                    >
                        To Sale
                    </Button>

                    {/* Back To List Button */}
                    <Button
                        variant="contained"
                        component={Link}
                        to="/ProformaIndex"
                        className="linkcolor"
                        onClick={() => localStorage.setItem("shouldReload", "true")}
                        style={{
                            height: "25px",
                            minWidth: "90px",
                            borderRadius: "6px",
                            backgroundColor: "#F3F4F6",
                            color: "#1E3A8A",
                            fontWeight: 600,
                            fontSize: "13px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            textTransform: "none",
                        }}
                    >
                        Back To List
                    </Button>
                </div>
            </div>

            {/* Top Section - Supplier & Bill */}

            <Modal open={isModalOpenverify} onClose={closeModalverify} style={{ marginTop: '10%' }}>
                <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '8px', margin: 'auto', width: '30%', textAlign: 'center' }}>
                    <h5>Convert to Sale   🤔!</h5>
                    <p>Are you sure , You want to Convert this Data ?</p>
                    <Button
                        style={{ margin: '8px', color: 'darkgreen' }}
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : <b>Convert To Sale</b>}
                    </Button>
                    <Button
                        style={{ margin: '8px', color: 'brown' }}
                        onClick={closeModalverify}
                    >
                        <b>Cancel</b>
                    </Button>
                </div>
            </Modal>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(150px, 1.5fr) repeat(9, minmax(80px, 1fr))",
                    gap: "10px",
                    marginBottom: "13px",
                    alignItems: "end",
                }}
            >
                {/* Sale Man */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Sale Man</label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        value={formData.SalesManName}
                        type="text"
                        placeholder="SalesManName"
                        name='SalesManName'
                        readOnly
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "13px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                        }}
                    />

                </div>

                {/* Freight */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>
                        Freight
                        <span style={{ color: "darkmagenta", marginLeft: "10px" }}>
                            ({formData.FreightGSTRate}%)
                        </span>
                        <span style={{ color: "red", marginLeft: "10px" }}>
                            {formData.FreightGSTAmount}
                        </span>
                    </label>
                    <input
                        type="number"
                        name="FreightAmount"
                        value={formData.FreightAmount}
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "13px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right",
                            fontWeight: "bold",
                        }}
                    />
                </div>

                {/* Packing */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>
                        Packing
                        <span style={{ color: "darkmagenta", marginLeft: "10px" }}>
                            ({formData.PackingGSTRate}%)
                        </span>
                        <span style={{ color: "red", marginLeft: "10px" }}>
                            {formData.PackingGSTAmount}
                        </span>
                    </label>
                    <input
                        type="number"
                        name="PackingAmount"
                        value={formData.PackingAmount}
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "13px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right",
                            fontWeight: "bold",
                        }}
                    />
                </div>

                {/* TCS */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>TCS</label>
                    <input
                        type="number"
                        name="TCSAmount"
                        value={formData.TCSAmount}
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "13px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right",
                            fontWeight: "bold",
                        }}
                    />
                </div>

                {/* Round Off */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Round Off</label>
                    <input
                        type="number"
                        name="RoundOff"
                        value={formData.RoundOff}
                        readOnly
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "13px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right",
                            fontWeight: "bold",
                        }}
                    />
                </div>

                {/* Total */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Total</label>
                    <input
                        type="number"
                        name="GrandTotal"
                        value={formData.GrandTotal}
                        readOnly
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "15px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right",
                            fontWeight: "bold",
                            color: "#0D47A1",
                            fontWeight: "bold",
                        }}
                    />
                </div>

                {/* Cash */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Cash</label>
                    <input
                        type="number"
                        name="CashAmount"
                        placeholder="CashAmount"
                        value={formData.CashAmount}
                        min="0"
                        max={Math.max(
                            0,
                            parseFloat(formData.GrandTotal || 0) -
                            (parseFloat(formData.CardAmount || 0) +
                                parseFloat(formData.UPIAmount || 0))
                        )}
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "15px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right",
                            fontWeight: "bold",
                        }}
                    />
                </div>

                {/* Card */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Card</label>
                    <input
                        type="number"
                        name="CardAmount"
                        placeholder="CardAmount"
                        value={formData.CardAmount}
                        min="0"
                        max={Math.max(
                            0,
                            parseFloat(formData.GrandTotal || 0) -
                            (parseFloat(formData.CashAmount || 0) +
                                parseFloat(formData.UPIAmount || 0))
                        )}
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "15px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right",
                            fontWeight: "bold",
                        }}
                    />
                </div>

                {/* UPI */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>UPI</label>
                    <input
                        type="number"
                        name="UPIAmount"
                        placeholder="UPIAmount"
                        value={formData.UPIAmount}
                        min="0"
                        max={Math.max(
                            0,
                            parseFloat(formData.GrandTotal || 0) -
                            (parseFloat(formData.CashAmount || 0) +
                                parseFloat(formData.CardAmount || 0))
                        )}
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "15px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right",
                            fontWeight: "bold",
                        }}
                    />
                </div>

                {/* Balance */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Balance</label>
                    <input
                        type="text"
                        name="BalanceAmount"
                        placeholder="BalanceAmount"
                        value={formData.BalanceAmount}
                        readOnly
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "15px",
                            height: "28px",
                            width: "100%",
                            padding: "0 6px",
                            textAlign: "right",
                            fontWeight: "bold",
                            color: formData.BalanceAmount > 0 ? "red" : "inherit",
                        }}
                    />
                </div>
            </div>


            {/* Amount Summary */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(6, 1fr)",
                    gap: "8px",
                    marginBottom: "13px",
                    fontSize: "13px"
                }}
            >
                <div style={{ fontWeight: "bold" }}>
                    Amount:{" "}
                    <span style={{ color: "#B71C1C", fontWeight: "bold" }}>
                        {formData.TotalAmount}
                    </span>
                </div>
                <div style={{ fontWeight: "bold" }}>
                    Discount:{" "}
                    <span style={{ color: "#210d0dff", fontWeight: "bold" }}>
                        {formData.DiscountAmount}
                    </span>
                </div>
                <div style={{ fontWeight: "bold" }}>
                    Taxable:{" "}
                    <span style={{ color: "#B71C1C", fontWeight: "bold" }}>
                        {formData.TaxableAmount}
                    </span>
                </div>
                <div style={{ fontWeight: "bold" }}>
                    CGST: <span style={{ color: "#B71C1C", fontWeight: "bold", }}>{formData.CGSTAmount}</span>
                </div>
                <div style={{ fontWeight: "bold" }}>
                    SGST: <span style={{ color: "#B71C1C", fontWeight: "bold", }}>{formData.SGSTAmount}</span>
                </div>
                <div style={{ fontWeight: "bold" }}>
                    IGST: <span style={{ color: "#B71C1C", fontWeight: "bold", }}>{formData.IGSTAmount}</span>
                </div>
            </div>

            {/* Data Table */}
            <div
                style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    marginBottom: "13px",
                    border: "1px solid #999",
                    borderRadius: "4px",
                    backgroundColor: "#fff",
                }}
            >
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "13px",
                        fontFamily: "monospace",
                    }}
                >
                    <thead style={{ backgroundColor: "#1F2A38", color: "#FFFFFF" }}>
                        <tr>
                            {[
                                "SL.No",
                                "Item",
                                "Qty",
                                "Rate",
                                "Amount",
                                "Disc%",
                                "Disc₹",
                                "Taxable",
                                "GST₹",
                                "Total",
                            ].map((header, idx) => (
                                <th
                                    key={idx}
                                    style={{
                                        padding: "3px",
                                        //textAlign: idx > 1 & 9 ? "right" : "left",
                                        textAlign: idx > 1 ? "center" : "left",
                                        borderBottom: "1px solid #ccc",
                                        textTransform: 'uppercase',
                                        fontSize: '10px'
                                    }}                                >
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.map((item, index) => (
                            <tr
                                key={index}
                                style={{
                                    backgroundColor: index % 2 === 0 ? "#FDFDFD" : "#F5F7FA",
                                    borderBottom: "1px solid #ddd",
                                }}
                            >
                                {/* SL.No + Delete */}
                                <td style={{ padding: "4px 6px", textAlign: "center" }}>
                                    {index + 1}
                                </td>

                                {/* Item Name */}
                                <td
                                    style={{
                                        fontSize: "13px",
                                        //fontWeight: "bold",
                                        padding: "4px",
                                        minWidth: "200px",
                                    }}
                                >
                                    {item.ItemName}
                                </td>

                                {/* Quantity */}
                                <td style={{ padding: "2px", textAlign: "right", width: "70px" }}>
                                    <input
                                        type="number"
                                        value={item.Quantity}
                                        name="Quantity"
                                        style={{
                                            width: "100%",
                                            textAlign: "right",
                                            fontWeight: "bold",
                                            border: "1px solid #bbb",
                                            borderRadius: "3px",
                                            padding: "2px",
                                        }}
                                    />
                                </td>

                                {/* Rate */}
                                <td style={{ padding: "2px", textAlign: "right", width: "120px" }}>
                                    <input
                                        type="number"
                                        value={item.Rate || 0}
                                        name="Rate"
                                        style={{
                                            width: "100%",
                                            textAlign: "right",
                                            fontWeight: "bold",
                                            border: "1px solid #bbb",
                                            borderRadius: "3px",
                                            padding: "2px",
                                        }}
                                    />
                                </td>

                                {/* Amount */}
                                <td style={{ padding: "2px", textAlign: "right", width: "120px" }}>
                                    <input
                                        value={item.Amount || 0}
                                        readOnly
                                        style={{
                                            width: "100%",
                                            textAlign: "right",
                                            border: "1px solid #ccc",
                                            fontWeight: "bold",
                                            padding: "2px",
                                            borderRadius: "3px",
                                        }}
                                    />
                                </td>

                                {/* Discount % */}
                                <td style={{ padding: "2px", textAlign: "right", width: "120px" }}>
                                    <input
                                        type="number"
                                        value={item.DiscountPercentage || 0}
                                        name="DiscountPercentage"
                                        style={{
                                            width: "100%",
                                            textAlign: "right",
                                            fontWeight: "bold",
                                            border: "1px solid #bbb",
                                            borderRadius: "3px",
                                            padding: "2px",

                                        }}
                                    />
                                </td>

                                {/* Discount Amount */}
                                <td style={{ padding: "2px", textAlign: "right", width: "120px" }}>
                                    <input
                                        value={item.DiscountAmount || 0}
                                        readOnly
                                        style={{
                                            width: "100%",
                                            textAlign: "right",
                                            border: "1px solid #ccc",
                                            borderRadius: "3px",
                                            padding: "2px",
                                            fontWeight: "bold",
                                        }}
                                    />
                                </td>

                                {/* Taxable */}
                                <td style={{ padding: "2px", textAlign: "right", width: "120px" }}>
                                    <input
                                        value={item.TaxableAmount || 0}
                                        readOnly
                                        style={{
                                            width: "100%",
                                            textAlign: "right",
                                            border: "1px solid #ccc",
                                            borderRadius: "3px",
                                            padding: "2px",
                                            fontWeight: "bold",
                                        }}
                                    />
                                </td>

                                {/* GST */}
                                <td style={{ padding: "2px", textAlign: "right", width: "120px" }}>
                                    <input
                                        value={
                                            parseFloat(item.CGSTAmount) +
                                            parseFloat(item.SGSTAmount) +
                                            parseFloat(item.IGSTAmount)
                                        }
                                        readOnly
                                        style={{
                                            width: "70%",
                                            textAlign: "right",
                                            border: "1px solid #ccc",
                                            borderRadius: "3px",
                                            padding: "2px",
                                            fontWeight: "bold",
                                        }}
                                    />
                                    <span
                                        style={{
                                            marginLeft: "4px",
                                            color: "darkmagenta",
                                            fontSize: "11px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {item.GSTRate ? parseFloat(item.GSTRate) : 0}%
                                    </span>
                                </td>

                                {/* Total */}
                                <td style={{ padding: "2px", textAlign: "right", width: "120px" }}>
                                    <input
                                        value={item.TotalAmount || 0}
                                        readOnly
                                        style={{
                                            width: "100%",
                                            textAlign: "right",
                                            //backgroundColor: "#DDE9EC",
                                            border: "1px solid #888",
                                            borderRadius: "3px",
                                            padding: "2px",
                                            fontWeight: "bold",
                                            color: 'brown'
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Top Section - Remarks & Bank */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: formData.UPIAmount > 0 || formData.CardAmount > 0
                        ? "3fr 1fr" // show both side by side
                        : "1fr",    // only Remarks
                    gap: "8px",
                    //marginBottom: "13px",
                    fontSize: "13px",
                    alignItems: "end",
                }}
            >
                {/* Remarks */}
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Remarks</label>
                    <textarea
                        name="Remarks"
                        value={formData.Remarks}
                        rows={1}
                        style={{
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "13px",
                            width: "100%",
                            padding: "4px",
                            resize: "vertical",
                        }}
                    />
                </div>

                {/* Bank - conditional */}
                {(formData.UPIAmount > 0 || formData.CardAmount > 0) && (
                    <div>
                        <label style={{ fontSize: "11px", fontWeight: "600" }}>Bank</label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            value={formData.BankName}
                            type="text"
                            placeholder="BankName"
                            name='BankName'
                            readOnly
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                fontSize: "13px",
                                height: "28px",
                                width: "100%",
                                padding: "0 6px",
                            }}
                        />
                    </div>
                )}
            </div>


        </div>
    );
}
export default ProformaDetails;
